import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, CheckCircle2, ArrowRightCircle, UserPlus, IndianRupee, ChevronDown, ChevronRight, CalendarDays, Building2, Mail, Phone, Clock, Link as LinkIcon, MoreVertical, MessageCircle, Pencil, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { digitsForWhatsApp, copyText, quoteMessage } from "@/components/procurement/ShareQuoteLink";
import { VendorContactDialog } from "@/components/procurement/VendorContactDialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";
import { Link2 } from "lucide-react";
import { CompanyField } from "@/components/CompanyField";

import { confirmAction } from "@/lib/dialogs";
const PROCUREMENT_WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "", wbs_item_id: "" };

export default function RFQs() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = PROCUREMENT_WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [rfqs, setRfqs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const [materialsByWbsId, setMaterialsByWbsId] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [rfqRes, vendorRes, prRes] = await Promise.all([
        api.get("/procurement/rfqs"), api.get("/procurement/vendors"), api.get("/procurement/requisitions").catch(() => ({ data: [] })),
      ]);
      setRfqs([...rfqRes.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      setVendors(vendorRes.data);
      setRequisitions(prRes.data);
      const projectIds = [...new Set(rfqRes.data.map((r) => r.project_id).filter(Boolean))];
      const lookups = await Promise.all(projectIds.map((pid) =>
        api.get(`/projects/${pid}/wbs`).then(({ data: wbs }) => wbs.map((w) => [w.id, `${w.code} — ${w.name}`])).catch(() => [])
      ));
      setMaterialsByWbsId(Object.fromEntries(lookups.flat()));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  const opened = useRef(false);
  useEffect(() => { if (!opened.current && rfqs.length) { opened.current = true; setExpanded({ [rfqs[0].id]: true }); } }, [rfqs]);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || id;

  const send = async (rfq) => {
    setBusyId(rfq.id);
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/send`);
      toast.success("RFQ sent");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const select = async (rfq, vendorId) => {
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/vendors/${vendorId}/select`);
      toast.success("Vendor selected");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const remove = async (rfq) => {
    if (!await confirmAction(`Delete RFQ ${rfq.number}? This can't be undone.`)) return;
    try {
      await api.delete(`/procurement/rfqs/${rfq.id}`);
      toast.success(`RFQ ${rfq.number} deleted`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const convert = async (rfq) => {
    const selected = rfq.invited_vendors.find((v) => v.is_selected);
    if (!selected) { toast.error("Select a vendor first"); return; }
    setBusyId(rfq.id);
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/convert-to-po`, { vendor_id: selected.vendor_id, po_date: new Date().toISOString().slice(0, 10) });
      toast.success("Converted to Purchase Order");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="rfqs-page">
      <PageHeader title="RFQs" description="Request quotations from invited vendors, record their quotes, then convert the best one into a PO." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreateRFQDialog pfProjects={pf.projects} companies={companies} vendors={vendors} onCreated={load} />}</div>} />
      {!loading && rfqs.length === 0 ? (
        <EmptyState title="No RFQs yet" testId="rfqs-empty-state" />
      ) : (
        <div className="space-y-4">
          {pf.apply(rfqs, (r) => r.project_id).map((rfq) => {
            const locked = ["CLOSED", "CONVERTED"].includes(rfq.status);
            const live = !["CLOSED", "CONVERTED", "CANCELLED"].includes(rfq.status);
            const open = !!expanded[rfq.id];
            return (
            <div key={rfq.id} className="rounded-2xl border border-border bg-card shadow-sm" data-testid={`rfq-card-${rfq.number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 p-4 sm:p-5">
                <button type="button" className="flex min-w-0 items-start gap-3 text-left" onClick={() => toggleExpand(rfq.id)} aria-expanded={open} data-testid={`rfq-toggle-${rfq.number}`}>
                  {open ? <ChevronDown className="mt-1.5 h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="mt-1.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                  <span className="min-w-0">
                    <span className="block font-heading text-lg font-semibold leading-tight text-foreground">{rfq.number}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5" /> {rfq.rfq_date}<span aria-hidden>•</span>{new Date(rfq.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                      {rfq.due_date && <span className="ml-1">· due {rfq.due_date}</span>}
                    </span>
                    {rfq.requisition_id && <span className="mt-0.5 block text-xs text-sky-600 dark:text-sky-400">From requisition {requisitions.find((r) => r.id === rfq.requisition_id)?.number || "—"}</span>}
                  </span>
                </button>
                <div className="flex flex-wrap items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                  <StatusPill value={rfq.status} />
                  {canWrite && live && <InviteVendorDialog rfq={rfq} vendors={vendors} vendorName={vendorName} onInvited={load} />}
                  {canWrite && rfq.status === "DRAFT" && <Button size="sm" variant="outline" disabled={busyId === rfq.id} onClick={() => send(rfq)} data-testid={`send-rfq-button-${rfq.number}`}><Send className="h-3.5 w-3.5" /> Send</Button>}
                  {canWrite && ["DRAFT", "SENT"].includes(rfq.status) && rfq.invited_vendors.some((v) => v.is_selected) && <Button size="sm" variant="outline" disabled={busyId === rfq.id} onClick={() => convert(rfq)} data-testid={`convert-rfq-button-${rfq.number}`}><ArrowRightCircle className="h-3.5 w-3.5" /> Convert to PO</Button>}
                  {canWrite && ["DRAFT", "SENT", "CANCELLED"].includes(rfq.status) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" title="More" data-testid={`rfq-menu-${rfq.number}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem className="text-rose-600 focus:text-rose-600" onSelect={() => remove(rfq)} data-testid={`delete-${rfq.number}`}><Trash2 className="h-4 w-4" /> Delete RFQ</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              {open && (
                <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">
                  {locked && (
                    <p className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground" data-testid={`rfq-closed-note-${rfq.number}`}>
                      A purchase order was made from this RFQ, so the quotes are locked. Cancel or delete that purchase order to reopen it.
                    </p>
                  )}
                  <RFQVendorTable rfq={rfq} vendors={vendors} vendorName={vendorName} companyName={(companies || []).find((c) => c.id === rfq.company_id)?.name}
                    canWrite={canWrite} live={live} locked={locked} onSelect={(vid) => select(rfq, vid)} onChanged={load} />
                  <details className="group rounded-xl border border-border/70 bg-background" data-testid={`rfq-items-${rfq.number}`}>
                    <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2.5 text-xs font-medium text-muted-foreground hover:text-foreground">
                      <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" /> Items requested ({rfq.items.length})
                    </summary>
                    <div className="overflow-x-auto border-t border-border/70 px-4 py-2">
                      <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-wide text-muted-foreground/70">
                          <tr><th className="pb-1 pr-3 text-left">Description</th><th className="pb-1 pr-3 text-right">Qty</th><th className="pb-1 pr-3 text-right">Est. rate</th><th className="pb-1 text-left">Linked material</th></tr>
                        </thead>
                        <tbody>
                          {rfq.items.map((it) => (
                            <tr key={it.id} className="border-t border-border/50">
                              <td className="py-1.5 pr-3 text-foreground">{it.description}</td>
                              <td className="py-1.5 pr-3 text-right font-mono">{Number(it.quantity)}</td>
                              <td className="py-1.5 pr-3 text-right font-mono">{it.estimated_rate ? `₹${it.estimated_rate}` : "—"}</td>
                              <td className="py-1.5">
                                {it.wbs_item_id && (
                                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-700 dark:text-emerald-400" data-testid={`rfq-item-material-link-${it.id}`}>
                                    <Link2 className="h-2.5 w-2.5" /> {materialsByWbsId[it.wbs_item_id] || "linked material"}
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const inr2 = (n) => `₹ ${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const stampDT = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true }) : "");

function ResponseBadge({ rfq, iv }) {
  if (iv.submitted_at) return <><Pill tone="ok" icon={CheckCircle2}>Quoted</Pill><p className="mt-1 text-[11px] text-muted-foreground">{stampDT(iv.submitted_at)}</p></>;
  if (iv.viewed_at) return <><Pill tone="ok" icon={CheckCircle2}>Opened</Pill><p className="mt-1 text-[11px] text-muted-foreground">{stampDT(iv.viewed_at)}</p></>;
  if (rfq.status === "DRAFT") return <><Pill tone="idle" icon={Clock}>Not sent yet</Pill><p className="mt-1 text-[11px] text-muted-foreground">—</p></>;
  return <><Pill tone="idle" icon={Clock}>Link not opened yet</Pill><p className="mt-1 text-[11px] text-muted-foreground">—</p></>;
}

function Pill({ tone, icon: Icon, children }) {
  const cls = tone === "ok" ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-muted text-muted-foreground";
  return <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}><Icon className="h-3.5 w-3.5" />{children}</span>;
}

function RFQVendorTable({ rfq, vendors, vendorName, companyName, canWrite, live, locked, onSelect, onChanged }) {
  const [quoteFor, setQuoteFor] = useState(null);   // invite whose quote is being typed in
  const [contactFor, setContactFor] = useState(null); // vendor whose phone/email is being edited
  const [mailing, setMailing] = useState(null); // invite id being emailed right now
  const [mailed, setMailed] = useState({}); // invite ids emailed in this session
  const sent = rfq.status === "SENT";
  const quotes = rfq.invited_vendors.filter((v) => v.quoted_amount != null).map((v) => Number(v.quoted_amount));
  const lowestAmt = quotes.length > 1 ? Math.min(...quotes) : null;
  const rows = [...rfq.invited_vendors].sort((a, b) => {
    const qa = a.quoted_amount != null ? Number(a.quoted_amount) : Infinity;
    const qb = b.quoted_amount != null ? Number(b.quoted_amount) : Infinity;
    return qa - qb;
  });

  const emailLink = async (iv, email) => {
    setMailing(iv.id);
    try {
      const { data } = await api.post(`/procurement/rfqs/${rfq.id}/vendors/${iv.vendor_id}/send-email`, { base_url: window.location.origin });
      setMailed((m) => ({ ...m, [iv.id]: true }));
      toast.success(`Quote link emailed to ${data.sent_to || email}`);
      if (data.local_link) toast.warning("The link points to localhost, so the vendor cannot open it from outside. Set APP_BASE_URL on the server or use the public address.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setMailing(null);
    }
  };

  const allowRevision = async (iv) => {
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/vendors/${iv.vendor_id}/reopen-quote`);
      toast.success(`${vendorName(iv.vendor_id)} can now revise their quote`);
      onChanged?.();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  return (
    <section className="rounded-xl border border-border/70 bg-muted/30 p-3 sm:p-4" data-testid={`rfq-vendors-${rfq.number}`}>
      <h3 className="mb-3 text-sm font-semibold text-foreground">Vendor Quotations</h3>
      {rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">No vendors invited yet. Use Invite Vendor to add some.</p>
      ) : (
      <div className="overflow-x-auto rounded-xl border border-border/70 bg-card">
        <table className="w-full min-w-[920px] text-sm">
          <thead>
            <tr className="border-b border-border/70 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="w-[24%] px-4 py-2.5 text-left">Vendor</th><th className="w-[22%] px-4 py-2.5 text-left">Quoted price (₹)</th>
              <th className="w-[18%] px-4 py-2.5 text-left">Response</th><th className="w-[24%] px-4 py-2.5 text-left">Contact</th><th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((iv) => {
              const vendor = vendors.find((v) => v.id === iv.vendor_id);
              const name = vendorName(iv.vendor_id);
              const id = `${rfq.number}-${iv.vendor_id}`;
              const lowest = lowestAmt != null && iv.quoted_amount != null && Number(iv.quoted_amount) === lowestAmt;
              const phone = digitsForWhatsApp(vendor?.phone);
              const email = (vendor?.email || "").trim();
              const link = iv.access_token ? `${window.location.origin}/rfq/${iv.access_token}` : "";
              const message = quoteMessage({ rfq, vendor, link, companyName });
              const doCopy = async () => { if (await copyText(link)) toast.success("Quote link copied"); else toast.error("Could not copy. Select the link and copy it by hand."); };
              return (
                <tr key={iv.id} className={`border-b border-border/60 last:border-0 ${lowest ? "bg-emerald-500/[0.07]" : ""}`} data-testid={`rfq-vendor-row-${id}`}>
                  <td className="px-4 py-3.5 align-top">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground"><Building2 className="h-4 w-4" /></span>
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{name}</p>
                        {vendor?.contact_person && <p className="text-xs text-muted-foreground">{vendor.contact_person}</p>}
                        {iv.is_selected && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary" data-testid={locked ? `rfq-awarded-${id}` : `rfq-selected-${id}`}><CheckCircle2 className="h-3 w-3" />{locked ? "Awarded" : "Selected"}</span>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5 align-top">
                    {iv.quoted_amount != null ? (
                      <div>
                        <span className="whitespace-nowrap font-semibold tabular-nums text-foreground">{inr2(iv.quoted_amount)}</span>
                        {lowest && <span className="ml-2 inline-block whitespace-nowrap rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">Lowest bid</span>}
                        {rfq.items.length > 1 && !iv.line_rates && <p className="text-[11px] text-amber-600 dark:text-amber-400">total only, no item rates</p>}
                      </div>
                    ) : <span className="text-muted-foreground">No quote yet</span>}
                  </td>
                  <td className="px-4 py-3.5 align-top"><ResponseBadge rfq={rfq} iv={iv} /></td>
                  <td className="px-4 py-3.5 align-top text-[13px]" data-testid={`rfq-contact-${id}`}>
                    <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{email ? <span className="text-foreground [overflow-wrap:anywhere]">{email}</span> : canWrite ? <button type="button" className="text-amber-600 hover:underline dark:text-amber-400" onClick={() => setContactFor(vendor)}>Add email</button> : <span className="text-muted-foreground">—</span>}</p>
                    <p className="mt-1.5 flex items-center gap-2"><Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{vendor?.phone ? <span className="text-foreground">{vendor.phone}</span> : canWrite ? <button type="button" className="text-amber-600 hover:underline dark:text-amber-400" onClick={() => setContactFor(vendor)}>Add phone</button> : <span className="text-muted-foreground">—</span>}</p>
                  </td>
                  <td className="px-4 py-3.5 align-top">
                    <div className="flex items-center justify-end gap-1">
                      {sent && link && <Button size="sm" variant="ghost" className="h-8 gap-1.5 px-2.5" onClick={doCopy} title="Copy this vendor's private quote link" data-testid={`rfq-copy-link-${id}`}><LinkIcon className="h-4 w-4" /> Link</Button>}
                      {sent && link && (email
                        ? <Button size="icon" variant="ghost" className="h-8 w-8" disabled={mailing === iv.id} onClick={() => emailLink(iv, email)} title={mailed[iv.id] ? `Emailed to ${email}. Click to send again` : `Email the quote link to ${email}`} data-testid={`rfq-email-${id}`}>{mailing === iv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : mailed[iv.id] ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Mail className="h-4 w-4" />}</Button>
                        : <Button size="icon" variant="ghost" className="h-8 w-8" disabled title="Add the vendor's email first" data-testid={`rfq-email-${id}`}><Mail className="h-4 w-4" /></Button>)}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" className="h-8 w-8" title="More actions" data-testid={`rfq-vendor-menu-${id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[200px]">
                          {sent && link && (phone
                            ? <DropdownMenuItem asChild><a href={`https://wa.me/${phone}?text=${encodeURIComponent(message)}`} target="_blank" rel="noreferrer" data-testid={`rfq-whatsapp-${id}`}><MessageCircle className="h-4 w-4" /> Send on WhatsApp</a></DropdownMenuItem>
                            : <DropdownMenuItem disabled data-testid={`rfq-whatsapp-${id}`}><MessageCircle className="h-4 w-4" /> WhatsApp (add phone first)</DropdownMenuItem>)}
                          {canWrite && live && <DropdownMenuItem onSelect={() => setQuoteFor(iv)} data-testid={`rfq-vendor-quote-button-${id}`}><IndianRupee className="h-4 w-4" /> {iv.quoted_amount != null ? "Edit quote" : "Enter quote"}</DropdownMenuItem>}
                          {canWrite && live && iv.submitted_at && sent && <DropdownMenuItem onSelect={() => allowRevision(iv)} data-testid={`rfq-allow-revision-${id}`}><Undo2 className="h-4 w-4" /> Allow revision</DropdownMenuItem>}
                          {canWrite && vendor && <DropdownMenuItem onSelect={() => setContactFor(vendor)} data-testid={`rfq-edit-contact-${id}`}><Pencil className="h-4 w-4" /> Edit phone / email</DropdownMenuItem>}
                          {canWrite && live && <><DropdownMenuSeparator /><DropdownMenuItem onSelect={() => onSelect(iv.vendor_id)} data-testid={`select-rfq-vendor-button-${id}`}><CheckCircle2 className="h-4 w-4" /> {iv.is_selected ? "Selected for award" : "Select this vendor"}</DropdownMenuItem></>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
      {quoteFor && <QuoteDialog rfq={rfq} invite={quoteFor} vendorName={vendorName(quoteFor.vendor_id)} onSaved={onChanged} open onOpenChange={(o) => !o && setQuoteFor(null)} />}
      {contactFor && <VendorContactDialog vendor={contactFor} onSaved={onChanged} open onOpenChange={(o) => !o && setContactFor(null)} />}
    </section>
  );
}

function InviteVendorDialog({ rfq, vendors, vendorName, onInvited }) {
  const [open, setOpen] = useState(false);
  const [vendorIds, setVendorIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const invitedIds = new Set(rfq.invited_vendors.map((v) => v.vendor_id));
  const available = (vendors || []).filter((v) => !invitedIds.has(v.id));
  const toggleVendor = (id) => setVendorIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const submit = async () => {
    if (vendorIds.length === 0) { toast.error("Select at least one vendor"); return; }
    setSaving(true);
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/vendors`, { vendor_ids: vendorIds });
      toast.success("Vendor(s) invited");
      setVendorIds([]);
      setOpen(false);
      onInvited?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`invite-vendor-button-${rfq.number}`}><UserPlus className="h-3.5 w-3.5" /> Invite Vendor</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Invite vendors to {rfq.number}</DialogTitle></DialogHeader>
        {available.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid={`invite-vendor-empty-${rfq.number}`}>All vendors have already been invited to this RFQ.</p>
        ) : (
          <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-lg border border-border p-2">
            {available.map((v) => (
              <label key={v.id} className="flex items-center gap-2 text-sm text-foreground" data-testid={`invite-vendor-checkbox-${rfq.number}-${v.code}`}>
                <Checkbox checked={vendorIds.includes(v.id)} onCheckedChange={() => toggleVendor(v.id)} /> {v.name}
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button disabled={saving || available.length === 0} onClick={submit} data-testid={`invite-vendor-submit-button-${rfq.number}`}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateRFQDialog({ companies, pfProjects, vendors, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", rfq_date: "", due_date: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [vendorIds, setVendorIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, estimated_rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.estimated_rate,
  } : it)));
  const toggleVendor = (id) => setVendorIds((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/rfqs", { ...form, project_id: form.project_id || null, due_date: form.due_date || null, items: items.map((it) => ({ ...it, wbs_item_id: it.wbs_item_id || null })), vendor_ids: vendorIds });
      toast.success("RFQ created");
      setForm({ company_id: "", rfq_date: "", due_date: "" });
      setItems([{ ...EMPTY_ITEM }]);
      setVendorIds([]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-rfq-button"><Plus className="h-4 w-4" /> New RFQ</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New RFQ</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-rfq-form">
          <div>
            <Label>Project</Label>
            <Select value={form.project_id || ""} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="rfqs-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(pfProjects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="rfq-company-select" />
            <div><Label>Date</Label><Input required type="date" data-testid="rfq-date-input" value={form.rfq_date} onChange={set("rfq_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="space-y-1 rounded-md border border-transparent p-1 hover:border-border" data-testid={`rfq-item-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} data-testid={`rfq-item-desc-input-${idx}`} />
                    <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`rfq-item-qty-input-${idx}`} />
                    {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`remove-rfq-item-button-${idx}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </div>
                  <div className="flex items-center gap-2 pl-0.5">
                    <span className="w-20 shrink-0 text-[11px] text-muted-foreground">WBS activity</span>
                    <div className="max-w-xs flex-1"><WbsItemSelect projectId={form.project_id} value={it.wbs_item_id} onChange={(v) => updateItem(idx, "wbs_item_id", v)} testId={`rfq-item-wbs-select-${idx}`} /></div>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-rfq-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <div>
            <Label>Invite vendors</Label>
            <div className="mt-1.5 space-y-1.5 max-h-32 overflow-y-auto rounded-lg border border-border p-2">
              {(vendors || []).map((v) => (
                <label key={v.id} className="flex items-center gap-2 text-sm text-foreground" data-testid={`rfq-invite-vendor-checkbox-${v.code}`}>
                  <Checkbox checked={vendorIds.includes(v.id)} onCheckedChange={() => toggleVendor(v.id)} /> {v.name}
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-rfq-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QuoteDialog({ rfq, invite, vendorName, onSaved, open: controlledOpen, onOpenChange }) {
  const [innerOpen, setInnerOpen] = useState(false);
  const controlled = controlledOpen !== undefined;
  const open = controlled ? controlledOpen : innerOpen;
  const setOpen = controlled ? onOpenChange : setInnerOpen;
  const [rates, setRates] = useState({});
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const existing = invite.line_rates || {};
    const single = rfq.items.length === 1 && invite.quoted_amount != null && !invite.line_rates
      ? { [rfq.items[0].id]: String((Number(invite.quoted_amount) / Number(rfq.items[0].quantity)).toFixed(2)) }
      : {};
    setRates(Object.fromEntries(rfq.items.map((it) => [it.id, existing[it.id] != null ? String(existing[it.id]) : single[it.id] || ""])));
    setRemarks(invite.remarks || "");
  }, [open, rfq, invite]);

  const total = rfq.items.reduce((sum, it) => sum + Number(it.quantity) * Number(rates[it.id] || 0), 0);
  const complete = rfq.items.every((it) => rates[it.id] !== "" && rates[it.id] != null);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/vendors/${invite.vendor_id}/quote`, { line_rates: rates, remarks: remarks || null });
      toast.success("Quote recorded");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" data-testid={`rfq-vendor-quote-button-${rfq.number}-${invite.vendor_id}`}>
            <IndianRupee className="h-3.5 w-3.5" /> {invite.quoted_amount != null ? "Edit quote" : "Enter quote"}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Quote from {vendorName}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3.5">
          <div className="space-y-2">
            <Label>Rate quoted for each item</Label>
            {rfq.items.map((it) => (
              <div key={it.id} className="flex items-center gap-2 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-foreground">{it.description}</div>
                  <div className="text-[11px] text-muted-foreground">Qty {it.quantity}{it.estimated_rate ? ` · estimated ₹${it.estimated_rate}` : ""}</div>
                </div>
                <Input required type="number" step="0.01" min="0" placeholder="Rate" className="w-28" value={rates[it.id] ?? ""}
                  onChange={(e) => setRates({ ...rates, [it.id]: e.target.value })} data-testid={`rfq-quote-rate-${it.id}`} />
              </div>
            ))}
          </div>
          <div><Label>Remarks</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Delivery time, validity, terms…" className="mt-1.5" /></div>
          <div className="rounded-lg bg-muted/50 p-3 text-sm font-medium text-foreground">Total quote: ₹{total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !complete}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save quote</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
